import type { IDisposable } from '@/engine/Utilities/IDisposable';
import ImageStackModule from '../../../../WasmModules/ImageStack';
import type * as ImageStackTypes from '../../../../WasmModules/ImageStack';
import { createImageStackLoadingTask, type IImageStackLoadingTask } from './ImageStackLoader';
import type { ImageStackUrlResolver } from './ImageStackUrlResolver';
import * as THREE from 'three';
import { ImageStackSlicer } from './ImageStackSlicer';

interface IContext {
  imageStack: ImageStackTypes.ImageStack;
  isReduced: boolean;
  gvMinMax: { min: number; max: number };
  size: THREE.Vector3;
  spacings: THREE.Vector3;
  cs: THREE.Matrix4;
}

export enum XRayEffect {
  Addition = 'Addition',
  Subtraction = 'Subtraction',
  AlphaBlending = 'AlphaBlending'
}

export class ImageStack implements IDisposable {
  private readonly _id: string;

  private readonly _urlResolver: ImageStackUrlResolver;

  private readonly _contrastWindowDef?: {
    min: number;
    max: number;
  };

  private _loadingTask?: IImageStackLoadingTask;

  private _context?: IContext;

  public constructor(
    id: string,
    urlResolver: ImageStackUrlResolver,
    contrastWindowDef?: {
      min: number;
      max: number;
    }
  ) {
    this._id = id;
    this._urlResolver = urlResolver;
    this._contrastWindowDef = contrastWindowDef;
  }

  public dispose(): void {
    this._loadingTask?.interrupt();
    this._loadingTask = undefined;
    this._context?.imageStack.dispose();
    this._context = undefined;
  }

  public isReady(): boolean {
    return !!this._context;
  }

  public isLoadingIncomplete(): boolean {
    return !!this._loadingTask;
  }

  public async loadAsync(
    isReduced: boolean,
    progress?: (processedCnt: number, totalCnt: number) => void,
    status?: (message: string) => void
  ): Promise<void> {
    if (this._loadingTask) {
      throw new Error('Loading is in process');
    }

    const urls = await this._urlResolver.resolveAsync(this._id, isReduced);

    this._loadingTask = createImageStackLoadingTask(urls.headerUrl, urls.dataUrl, progress, status);

    try {
      const imageStack = await this._loadingTask.promise;
      if (this._contrastWindowDef) {
        imageStack.setMeaningfulGVMinMax(this._contrastWindowDef.min, this._contrastWindowDef.max);
      }
      this._context?.imageStack.dispose(); // [chebotarev] cleanup resources from previous successful load
      this._context = {
        imageStack: imageStack,
        isReduced: isReduced,
        gvMinMax: {
          min: imageStack.getGVMin(),
          max: imageStack.getGVMax()
        },
        size: new THREE.Vector3().fromArray(imageStack.getSize()),
        spacings: new THREE.Vector3().fromArray(imageStack.getSpacings()),
        cs: new THREE.Matrix4().fromArray(imageStack.getCS())
      };
    } finally {
      this._loadingTask = undefined;
    }
  }

  public suspendLoading(): void {
    this._loadingTask?.suspend();
  }

  public resumeLoading(): void {
    this._loadingTask?.resume();
  }

  private get _safeContext(): IContext {
    if (!this._context) {
      throw new Error('Not initialized yet');
    }
    return this._context;
  }

  public isReduced(): boolean {
    return this._safeContext.isReduced;
  }

  // ------------------ Wrapped methods -------------------------
  public addXRayMesh(
    descriptor: string,
    bufferGeometry: THREE.BufferGeometry,
    contrastValue: number,
    tolerance: number
  ): void {
    if (!bufferGeometry.index) {
      throw new Error('Invalid mesh');
    }
    const vertices = Float32Array.from(bufferGeometry.attributes.position.array);
    const indices = Int32Array.from(bufferGeometry.index.array);

    this._safeContext.imageStack.addXRayMesh(descriptor, vertices, indices, contrastValue, tolerance);
  }

  public getGVMinMax(): { min: number; max: number } {
    return this._safeContext.gvMinMax;
  }

  public getComponentSize(): number {
    return this._safeContext.imageStack.getComponentSize();
  }

  public getSize(): THREE.Vector3 {
    return this._safeContext.size;
  }

  public getSpacings(): THREE.Vector3 {
    return this._safeContext.spacings;
  }

  public getCS(): THREE.Matrix4 {
    return this._safeContext.cs;
  }

  public getSlicingCS(orientation: string): THREE.Matrix4 {
    return new THREE.Matrix4().fromArray(this._safeContext.imageStack.getSlicingCS(orientation));
  }

  public getReductionFactor(): number {
    let imageReductionFactor = this._safeContext.imageStack.getReductionFactor();
    if (this.isReduced() && imageReductionFactor === 0.0) {
      // viewer 2.6 dev cases detection: reduced with 1.5 factor but no information about reduction inside
      imageReductionFactor = 1.5;
    }
    return imageReductionFactor;
  }

  public getImageSlicer(slicingCs: THREE.Matrix4): ImageStackSlicer {
    return new ImageStackSlicer(this._safeContext.imageStack.getImageSlicer(slicingCs.toArray()));
  }

  public getImageSlicerReduced(slicingCs: THREE.Matrix4, reductionFactor: number): ImageStackSlicer {
    return new ImageStackSlicer(
      this._safeContext.imageStack.getImageSlicerReduced(slicingCs.toArray(), reductionFactor)
    );
  }

  private _resolveXRayEffect(effect: XRayEffect): ImageStackTypes.XRayEffect {
    switch (effect) {
      case XRayEffect.Addition:
        return ImageStackModule.XRayEffect.Addition;
      case XRayEffect.Subtraction:
        return ImageStackModule.XRayEffect.Subtraction;
      case XRayEffect.AlphaBlending:
        return ImageStackModule.XRayEffect.AlphaBlending;
      default:
        throw new Error('Unknown XRay effect');
    }
  }

  public getXRayImageSlicer(
    slicingCs: THREE.Matrix4,
    xRayEffect: XRayEffect,
    xRaySliceThickness: number,
    xRayRescale: number,
    xRayReductionFactor: number
  ): ImageStackSlicer {
    return new ImageStackSlicer(
      this._safeContext.imageStack.getXRayImageSlicer(
        slicingCs.toArray(),
        this._resolveXRayEffect(xRayEffect),
        xRaySliceThickness,
        xRayRescale,
        xRayReductionFactor
      )
    );
  }

  public getXRayImageSlicerReduced(
    slicingCs: THREE.Matrix4,
    xRayEffect: XRayEffect,
    xRaySliceThickness: number,
    xRayRescale: number,
    xRayMaxSlicesCount: number,
    reductionFactor: number
  ): ImageStackSlicer {
    return new ImageStackSlicer(
      this._safeContext.imageStack.getXRayImageSlicerReduced(
        slicingCs.toArray(),
        this._resolveXRayEffect(xRayEffect),
        xRaySliceThickness,
        xRayRescale,
        xRayMaxSlicesCount,
        reductionFactor
      )
    );
  }
}
